import React, { useEffect, useState } from 'react';
import { carboneReportingService, assistantService, AssistantCarbone } from '../../api/services';
import { CockpitCarbone as CockpitData, RecommandationCarbone } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Cockpit carbone (TF-021/022) : intensité CO₂/€, produits les plus émetteurs, et
 * score carbone des fournisseurs (intensité kg CO₂e / € acheté, plus bas = mieux).
 */
export function CockpitCarbone() {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(iso(new Date(today.getFullYear(), 0, 1)));
  const [end, setEnd] = useState(iso(today));
  const [data, setData] = useState<CockpitData | null>(null);
  const [recos, setRecos] = useState<RecommandationCarbone[]>([]);
  const [loading, setLoading] = useState(true);
  const [assistant, setAssistant] = useState<AssistantCarbone | null>(null);
  const [assistantLoading, setAssistantLoading] = useState(false);

  const load = () => {
    setLoading(true);
    carboneReportingService.getCockpit(start, end).then(setData).finally(() => setLoading(false));
    carboneReportingService.getRecommandations().then(setRecos).catch(() => setRecos([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const kg = (v: number) => `${v.toFixed(1)} kg`;

  const lancerAssistant = () => {
    setAssistantLoading(true);
    assistantService.actionsCarbone().then(setAssistant).finally(() => setAssistantLoading(false));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Cockpit carbone</h1>
        <p className="page-subtitle">Intensité CO₂/€ et contributeurs, intégrés aux flux commerciaux</p>
      </div>

      <div className="card flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
          <input type="date" className="input-field" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
          <input type="date" className="input-field" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <button onClick={load} className="btn-primary">Actualiser</button>
      </div>

      {loading && <LoadingSpinner />}

      <div className="card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">Assistant carbone</h2>
            <p className="text-xs text-gray-500">3 actions chiffrées pour réduire le CO₂ sans perte de marge (TF-081)</p>
          </div>
          <button onClick={lancerAssistant} disabled={assistantLoading} className="btn-secondary">
            {assistantLoading ? 'Analyse…' : 'Proposer 3 actions'}
          </button>
        </div>
        {assistant && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md bg-emerald-50 border border-emerald-100 p-3">
              <p className="text-sm text-gray-700 whitespace-pre-line">{assistant.synthese}</p>
              <p className="text-[11px] text-gray-400 mt-2">
                {assistant.genereParIa ? 'Synthèse rédigée par l’assistant IA' : 'Synthèse déterministe (assistant IA indisponible)'}
              </p>
            </div>
            {assistant.actions.map((a, i) => (
              <div key={i} className={`border-l-4 rounded-md p-3 ${
                a.priorite === 'HAUTE' ? 'border-l-green-600 bg-green-50'
                  : a.priorite === 'MOYENNE' ? 'border-l-amber-500 bg-amber-50'
                  : 'border-l-gray-400 bg-gray-50'
              }`}>
                <p className="font-medium text-gray-800">{a.titre}</p>
                <p className="text-xs text-gray-600 mt-0.5">{a.detail}</p>
                {(a.gainIntensite != null || a.impactMargePct != null) && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    {a.gainIntensite != null && <span>Gain : {a.gainIntensite.toFixed(3)} kg CO₂e/TND. </span>}
                    {a.impactMargePct != null && <span>Impact marge : {a.impactMargePct > 0 ? '+' : ''}{a.impactMargePct.toFixed(1)} %.</span>}
                  </p>
                )}
              </div>
            ))}
            {assistant.actions.length === 0 && (
              <p className="text-xs text-gray-400">Aucune action « sans perte de marge » détectée pour l'instant.</p>
            )}
          </div>
        )}
      </div>

      {recos.length > 0 && (
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Recommandations de réduction (sans perte de marge)</h2>
          <div className="space-y-2">
            {recos.map((r, i) => (
              <div key={i} className={`border-l-4 rounded-md p-3 ${
                r.priorite === 'HAUTE' ? 'border-l-green-600 bg-green-50'
                  : r.priorite === 'MOYENNE' ? 'border-l-amber-500 bg-amber-50'
                  : 'border-l-gray-400 bg-gray-50'
              }`}>
                <p className="font-medium text-gray-800">{r.titre}</p>
                <p className="text-xs text-gray-600 mt-0.5">{r.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="card">
              <p className="text-sm text-gray-500">CO₂e ventes</p>
              <p className="text-2xl font-bold text-green-700">{kg(data.totalCo2Ventes)}</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">CO₂e achats (Scope 3)</p>
              <p className="text-2xl font-bold text-green-700">{kg(data.totalCo2Achats)}</p>
              {!!data.totalCo2Transport && (
                <p className="text-xs text-gray-500 mt-1">dont transport : {kg(data.totalCo2Transport)}</p>
              )}
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">CA HT</p>
              <MoneyDisplay amount={data.caHT} className="text-2xl font-bold" />
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Intensité CO₂ / €</p>
              <p className="text-2xl font-bold text-emerald-700">
                {data.intensiteParEuro == null ? '—' : `${data.intensiteParEuro.toFixed(3)} kg/TND`}
              </p>
            </div>
          </div>

          <div className="card overflow-x-auto">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Produits les plus émetteurs</h2>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Produit</th>
                <th className="px-4 py-2 text-right">CO₂e</th>
                <th className="px-4 py-2 text-right">Revenu HT</th>
                <th className="px-4 py-2 text-right">Intensité (kg/TND)</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.topProduits.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                    Aucune donnée carbone (renseignez les facteurs d'émission des articles)
                  </td></tr>
                )}
                {data.topProduits.slice(0, 15).map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2">{p.libelle}</td>
                    <td className="px-4 py-2 text-right">{kg(p.co2Kg)}</td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={p.revenuHT} size="sm" /></td>
                    <td className="px-4 py-2 text-right">{p.intensite == null ? '—' : p.intensite.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card overflow-x-auto">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              Score carbone fournisseurs <span className="text-xs font-normal text-gray-400">(intensité basse = meilleur)</span>
            </h2>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Fournisseur</th>
                <th className="px-4 py-2 text-right">CO₂e achats</th>
                <th className="px-4 py-2 text-right">Montant acheté HT</th>
                <th className="px-4 py-2 text-right">Intensité (kg/TND)</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.fournisseurs.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">Aucune donnée fournisseur</td></tr>
                )}
                {data.fournisseurs.map((f, i) => (
                  <tr key={f.id}>
                    <td className="px-4 py-2">
                      {i === 0 && f.intensite != null && <span className="mr-1">🥇</span>}{f.raisonSociale}
                    </td>
                    <td className="px-4 py-2 text-right">{kg(f.co2Kg)}</td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={f.montantAchatHT} size="sm" /></td>
                    <td className="px-4 py-2 text-right font-medium">{f.intensite == null ? '—' : f.intensite.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default CockpitCarbone;
